// A3 Issues Tracker overview — fixed title "BIM Issues Tracker", two card rows, one row per component.
// Data: issues_overview({code}). Row click → A4 Component summary. "+" → New issue drawer (component to pick).
// Header (Part 5): Publish history (clock-arrow icon → A3.2) + Export PDF (→ A3.1 → A3.1.1, editors only).
// Sign-off chip → A3.3 pop-up under the chip. Route #/p/:code/issues/history[/:prefix] opens A3.2 (Activity links).
// Project setup moved to the sidebar (round 13 #7). ↑ / ↓ move through the components; Enter opens one (#8).
// v0.7.1 (round 25 #15–#16, board A3): the two card rows became the master summary's donut + "Open issues by discipline" bars
// (IssuesSummary); the table has fixed column widths — only Component stretches, so discipline columns never shift.
import { useState } from "react";
import { useListKeys } from "../../../core/useListKeys";
import { ICONS } from "../../../core/ui/icons";
import { useRpc } from "../../../core/api/useRpc";
import { href, navigate, replaceRoute } from "../../../core/router";
import { canEdit } from "../../../core/access";
import { CodeChip, ErrorBox, Fab, IconButton, Loading, PageHeader } from "../../../core/ui/kit";
import { SignoffChip } from "../components/Cards";
import { donutParts } from "../print/PrintPages";
import type { Discipline, IssueCounts } from "../../../core/api/types";
import { AddIssueDrawer } from "../components/AddIssueDrawer";
import { DownloadDialog, ExportDialog, type ExportOptions } from "../components/ExportPdf";
import { PublishHistoryDialog, SignoffPopup } from "../components/PublishHistory";
import { MAX_DISCIPLINES } from "../logic";

export const MODULE_TITLE = "BIM Issues Tracker";   // fixed, not editable (02/Oct/26)

export function OverviewPage({ code, history }: { code: string; history?: string }) {
  const { data, error, loading, reload } = useRpc("issues_overview", { code });
  const [adding, setAdding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [download, setDownload] = useState<ExportOptions | null>(null);
  const [signoff, setSignoff] = useState<{ prefix: string; anchor: DOMRect } | null>(null);
  const showHistory = (prefix?: string) => replaceRoute({ name: "issues", code, history: prefix ?? "" });
  const closeHistory = () => replaceRoute({ name: "issues", code });
  const [hi, setHi] = useState<number | null>(null);
  const comps = data?.components ?? [];
  useListKeys({ count: comps.length, hi, setHi, active: !adding && !exporting && !download && !signoff && history === undefined, onEnter: (i) => navigate({ name: "component", code, prefix: comps[i].prefix }) });
  if (loading && !data) return <Loading />;
  if (error || !data) return <ErrorBox message={error ?? "Not found"} />;
  const { stats, disciplines } = data;
  const slots = Math.max(0, MAX_DISCIPLINES - disciplines.length);
  return (
    <>
      <PageHeader crumb={<>{data.project.name} <CodeChip code={data.project.code} /> › Issues Tracker</>}
        title={MODULE_TITLE}
        sub={data.access === "view" ? "View only — your role cannot edit issues on this project" : undefined}
        actions={<>
          <IconButton icon={ICONS.history} name="Publish history" onClick={() => showHistory()} />
          <button className="btn" disabled={!canEdit(data.access)} title={canEdit(data.access) ? undefined : "View only — your role cannot export PDFs"}
            onClick={() => setExporting(true)}>Export PDF</button>
        </>} />
      <IssuesSummary st={stats} disciplines={disciplines} />
      <section className="card table-card">
        <h2>Components</h2>
        <div className="scroll">
          <table data-testid="components-table" className="comp-table fixed">
            <colgroup><col style={{ width: 52 }} /><col /><col style={{ width: 56 }} />{Array.from({ length: MAX_DISCIPLINES }, (_, k) => <col key={k} style={{ width: 48 }} />)}
              <col style={{ width: 56 }} /><col style={{ width: 96 }} /></colgroup>
            <thead><tr>
              <th>Prefix</th><th>Component</th><th className="num">Total</th>
              {disciplines.map((d) => <th key={d.code} className="num" style={{ color: d.colour }}>{d.code}</th>)}
              {Array.from({ length: slots }, (_, k) => <th key={k} className="num faint">–</th>)}
              <th className="num">Open</th><th className="so-col">Sign-off</th>
            </tr></thead>
            <tbody>{data.components.map((c, k) => (
              <tr key={c.id} data-row={k} className={`clickable${k === hi ? " kbd" : ""}`} onClick={() => navigate({ name: "component", code, prefix: c.prefix })}>
                <td><a className="code" href={href({ name: "component", code, prefix: c.prefix })} onClick={(e) => e.stopPropagation()}>{c.prefix}</a></td>
                <td className="ellipsis" title={c.name}>{c.name}</td>
                <td className="num">{c.total}</td>
                {disciplines.map((d) => <td key={d.code} className="num">{c.open_by_discipline[d.code] ?? 0}</td>)}
                {Array.from({ length: slots }, (_, k) => <td key={k} className="num faint">–</td>)}
                <td className="num">{c.open}</td>
                <td className="so-col" onClick={(e) => e.stopPropagation()}>
                  <SignoffChip s={c.signoff} onOpen={(el) => setSignoff({ prefix: c.prefix, anchor: el.getBoundingClientRect() })} /></td>
              </tr>))}
            </tbody>
          </table>
        </div>
      </section>
      {canEdit(data.access) && <Fab label="Add issue" onClick={() => setAdding(true)} />}
      {adding && <AddIssueDrawer code={code} components={data.components} disciplines={disciplines} prefix="" today={data.today}
        onClose={() => setAdding(false)} onCreated={(prefix, issue) => navigate({ name: "issue", code, prefix, issue })} />}
      {exporting && <ExportDialog ov={data} onClose={() => setExporting(false)} onExport={(o) => { setExporting(false); setDownload(o); }} />}
      {download && <DownloadDialog code={code} ov={data} opts={download} onClose={(changed) => { setDownload(null); if (changed) reload(); }} />}
      {history !== undefined && <PublishHistoryDialog code={code} initial={history || undefined} onClose={closeHistory} />}
      {signoff && <SignoffPopup code={code} prefix={signoff.prefix} anchor={signoff.anchor} onClose={(changed) => { setSignoff(null); if (changed) reload(); }} />}
    </>
  );
}

/** A3 summary (board A3, same as the PDF master summary P0): status donut — outer ring Open + Critical · Closed · Voided, inner
 *  ring Overdue — with its key, and open issues by discipline as bars. */
export function IssuesSummary({ st, disciplines }: { st: IssueCounts; disciplines: Discipline[] }) {
  const d = donutParts(st), tot = Math.max(1, d.total), od = (d.overdue / tot) * 100;
  const max = Math.max(1, ...disciplines.map((x) => st.open_by_discipline[x.code] ?? 0));
  let start = 0;
  const key: [string, string, number, string][] = [["N", "Open", d.outer[0].n, "#EF9F27"], ["overdue", "Overdue", d.overdue, "#A32D2D"],
    ["closed", "Closed", d.outer[1].n, "#639922"], ["voided", "Voided", d.outer[2].n, "#c9c6bc"]];
  return (
    <section className="card a3-summary" data-a3-summary="1" data-testid="project-summary">
      <div className="a3-donut">
        <svg width="120" height="120" viewBox="0 0 120 120" role="img" aria-label="Issues by status">
          {d.outer.map((o) => { const pct = (o.n / tot) * 100; const el = (
            <circle key={o.key} cx="60" cy="60" r="52" fill="none" stroke={o.colour} strokeWidth="14" pathLength={100}
              strokeDasharray={`${pct.toFixed(2)} ${(100 - pct).toFixed(2)}`} strokeDashoffset={(-start).toFixed(2)} transform="rotate(-90 60 60)" />);
            start += pct; return el; })}
          <circle cx="60" cy="60" r="38" fill="none" stroke="#f0ede6" strokeWidth="7" />
          <circle cx="60" cy="60" r="38" fill="none" stroke="#A32D2D" strokeWidth="7" pathLength={100} strokeDasharray={`${od.toFixed(2)} ${(100 - od).toFixed(2)}`} transform="rotate(-90 60 60)" />
          <text x="60" y="60" textAnchor="middle" fontSize="20" fontWeight="600" fill="#1a1a18">{d.total}</text>
          <text x="60" y="75" textAnchor="middle" fontSize="10" fill="#5f5e5a">issues</text>
        </svg>
        <div className="a3-key">{key.map(([k, label, n, c]) => <span key={k} data-stat={`project:${k}`} data-val={n}><i style={{ background: c }} />{label} <b>{n}</b></span>)}</div>
      </div>
      <div className="a3-bars">
        <p className="muted small">Open issues by discipline</p>
        <div className="a3-bar-grid">{disciplines.map((x) => {
          const n = st.open_by_discipline[x.code] ?? 0;
          return (
            <div key={x.code} className="a3-bar-row" data-bar={x.code} data-val={n}>
              <span className="ellipsis">{x.code} · <span className="muted">{x.name}</span></span>
              <div className="a3-bar"><div style={{ width: `${Math.round((n / max) * 100)}%`, background: x.colour }} /></div>
              <b>{n}</b>
            </div>);
        })}</div>
      </div>
    </section>);
}
